import configFile from "@/config";
import MOJ_APP_VERSION from "@/config/version.properties";
import constants from "@/constants";
import trackEvents from "@/events";
import EVENTS from "@/events/events";
import { AUTH_KEY } from "@/lib/common/constants";
import genericLandingPageStore from "@/stores/genericLandingPageStore";
import profileStore from "@/stores/profile";
import tagFeedStore from "@/stores/tagFeed";
import trendingFeedStore from "@/stores/trendingFeed";
import {
  getUserData,
  isBrowser,
  isProtectedRoute,
  nUserAgent,
  wait,
} from "@/utils";
import Cookies from "js-cookie";
import { get } from "svelte/store";
import gamificationStore from "../stores/gamificationStore";
import { fetcher } from "./fetcher.ts";

const {
  MOJ_USER_NAME,
  MOJ_CLIENT,
  MOJ_PHONE_NO,
  MOJ_USER_LANGUAGE,
  COUNTRY_AREA_CODE,
  FEED_LANGUAGE,
  BUDDING_CREATOR_PROGRAM_URL: buddingCreaterProgramUrl,
  WIDGET_TYPES,
  REQUEST_ACCOUNT_DELETION: requestAccountDeletionUrl,
  GAMIFICATION_PREFIX: gamificationPrefix,
  TROPHIES_DETAIL_KEY: trophiesDetailKey,
  CREATOR_JOURNEY_KEY: creatorJourneyKey,
  WEBVIEW_ENVIRONMENT,
  TROPHIES,
  TENANTS,
  RECENT_TROPHIES,
} = constants;

const { apiUrl, sanityApiUrl, sanityUpdateApiUrl, liveStreamVgServiceUrl } =
  configFile;

/**
 * @param {object} fetch
 * @param {string} url
 * @param {string} method
 * @param {object} body
 * @param {object} headers
 * @param {boolean} passCodeRequired
 * @param {boolean} returnOnlyRes
 * @returns {object}
 */
export async function apiRequest(
  fetch,
  url,
  method = "GET",
  body = {},
  headers = {},
  passCodeRequired = true,
  returnOnlyRes = false,
  skipAuth = false
) {
  let profileState = get(profileStore);
  const isMojLite = profileState.tenant === TENANTS.MOJTT;
  if (isBrowser()) {
    const { language } = getUserData();

    if (language) {
      headers["user-preferred-language"] = encodeURI(language);
    }
  }
  let requestBody = {};
  if (method !== "GET") {
    let newBody = body;
    if (passCodeRequired) {
      newBody = {
        ...body,
        userId: profileState.userId,
        passCode: profileState.passCode,
        client: "web",
      };
    }
    requestBody = {
      body: JSON.stringify({
        ...newBody,
      }),
    };
  }
  const request = {
    method,
    ...requestBody,
    headers: {
      "Content-Type": "application/json",
      "identity-version": "V2",
      "app-version": MOJ_APP_VERSION,
      baggage: isMojLite ? "x-tenant=mojtt" : "x-tenant=moj",
      "package-name": isMojLite
        ? "com.next.innovation.takatak"
        : "in.mohalla.video",
      "X-SHARECHAT-AUTH": profileState.accessToken || profileState.passCode,
      "X-SHARECHAT-AUTHORIZED-USERID": profileState.userId,
      "X-SHARECHAT-USERID": profileState.userId,
      "X-SHARECHAT-SECRET": profileState.passCode,
      "X-SHARECHAT-CALLER": "pwa-moj-service",
      "X-TENANT": profileState.tenant || "moj",
      ...headers,
    },
  };

  let response = await fetch(url, request);

  // retry after refreshing access token
  if (!skipAuth && response.status === 401 && profileState.isTempAuth) {
    await refreshAccessToken(fetch);
    const checkFlag = async () => {
      if (refreshAccessToken.refresh === "0") {
        await wait(100); // long poll to wait till refreshAccessToken.refresh is "1" or "-1"
        await checkFlag();
      } else if (
        refreshAccessToken.refresh === "1" || // successful refresh
        (refreshAccessToken.refresh === "-1" &&
          isBrowser() &&
          !isProtectedRoute(window?.location?.pathname)) // re-login with tempAuth
      ) {
        profileState = get(profileStore);
        request.headers = {
          ...request.headers,
          "X-SHARECHAT-AUTH": profileState.accessToken || profileState.passCode,
          "X-SHARECHAT-AUTHORIZED-USERID": profileState.userId,
          "X-SHARECHAT-USERID": profileState.userId,
          "X-SHARECHAT-SECRET": profileState.passCode,
          "X-TENANT": profileState.tenant || "moj",
        };
        response = await fetch(url, request);
      }
    };
    await checkFlag();
  }

  if (returnOnlyRes) {
    return response;
  }
  try {
    const resultJson = await response.json();
    return resultJson;
  } catch (e) {
    return e;
  }
}

/**
 *
 * @param {object} fetch
 * @param {string} url
 * @param {string} method
 * @param {object} body
 * @param {object} headers
 * @returns {object}
 */
export async function apiRequestWithAuth(
  fetch,
  url,
  method = "GET",
  body = {},
  headers = {}
) {
  const { appVersion, platform, userAuth } = getUserData();
  const request = {
    method,
    body: method !== "GET" ? body && JSON.stringify(body) : null,
    headers: {
      "Content-Type": "application/json",
      Authorization: userAuth,
      "client-type": platform,
      "app-version": appVersion,
      "RSA-ENCRYPTED-TOKEN": true,
      "X-SHARECHAT-CALLER": "pwa-moj-service",
      ...headers,
    },
  };
  const response = await fetch(url, request);
  const resultJson = await response.json();
  return resultJson;
}

/**
 *
 * @param {object} fetch
 */
export async function refreshAccessToken(fetch) {
  if (refreshAccessToken.refresh) return;
  refreshAccessToken.refresh = "0";
  const profileState = get(profileStore);
  const url = `${apiUrl}/account-service/v1.0.0/refreshToken`;
  const request = {
    method: "POST",
    body: JSON.stringify({
      access_token: profileState.accessToken,
      refresh_token: profileState.refreshToken,
    }),
    headers: {
      "Content-Type": "application/json",
      "X-SHARECHAT-USERID": `${profileState.userId}`,
    },
  };
  const response = await fetch(url, request);
  if (response.status === 200) {
    const resultJson = await response.json();
    const { access_token, refresh_token } = resultJson.payload;
    profileStore.updateState({
      accessToken: access_token,
      refreshToken: refresh_token,
    });
    refreshAccessToken.refresh = "1";
  } else {
    if (isProtectedRoute(window.location.pathname)) {
      await logout(true);
    } else {
      await signUpUser(fetch);
    }
    refreshAccessToken.refresh = "-1";
  }
  setTimeout(() => {
    refreshAccessToken.refresh = undefined;
  }, 10000);
}

/**
 *
 * @param {string} url
 * @param {object} payload
 */
export async function submitPostRequest(url, payload) {
  await window.fetch(url, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

/**
 *
 * @param {string} params
 * @param {object} payload
 */
export async function requestAccDeletionReactivation(
  params = "",
  payload = {}
) {
  const url = `${apiUrl}/${requestAccountDeletionUrl}/${params}`;
  const { userAuth } = getUserData();

  const request = {
    method: "POST",
    body: JSON.stringify(payload),
    headers: {
      "Content-Type": "application/json",
      AuthToken: userAuth,
      "RSA-ENCRYPTED-TOKEN": true,
      tenant: "moj",
      "X-SHARECHAT-CALLER": "pwa-moj-service",
    },
  };

  const response = await fetch(url, request);
  const resultJson = await response.json();
  return resultJson;
}

/**
 *
 * @param {object} fetch
 * @param {string} url
 * @param {object} query
 * @param {object} headers
 * @returns {object}
 */
export async function sanityRequest(fetch, url, query, headers = {}) {
  const request = {
    method: "POST",
    body: JSON.stringify({
      query,
    }),
    headers: {
      "Content-Type": "application/json",
      ...headers,
    },
  };
  // console.log({ url, request });

  const response = await fetch(url, request);
  const resultJson = await response.json();
  return resultJson;
}

/**
 *
 * @param {object} fetch
 * @param {array} mutations list of mutations that need to be updated
 * @returns
 */
export async function sanityPostRequest(fetch, mutations) {
  const request = {
    method: "POST",
    body: JSON.stringify({
      mutations,
    }),
    headers: {
      "Content-Type": "application/json",
      Authorization:
        "Bearer sk2oywkd5KCsJgZGbdnotYIIO0FVA3lRNpMHmOwqPTWw2bFynwdi3cAY4C4bkBKhm0it1Y9rzhdkXIZfesKlV7uuh81RyiDOYhpOLLi4PSHyRVhLoJYMyxYqTlAADX7cYwfLQdvGilcpuwpFRzls8EOvPd8nbg69VUFKau7MNp1AKWxWtLKt",
    },
  };
  const response = await fetch(sanityUpdateApiUrl, request);
  const resultJson = await response.json();
  return resultJson;
}

/**
 *
 * @param {object} fetch
 */
export async function signUpUser(fetch, tenant = "moj", signout = true) {
  const { deviceId, userId, isTempAuth } = get(profileStore);
  if (signout && isTempAuth) {
    await logout();
  }

  const eventPayload = {
    userId,
    deviceId,
    userAgent: nUserAgent(),
    cookie: Cookies.get(AUTH_KEY),
    traceId: Cookies.get("traceId"),
    tenant,
    source: "client",
  };

  trackEvents(EVENTS.SSR_EVENTS, eventPayload);

  const signupData = await apiRequest(
    fetch,
    `${apiUrl}/signUp`,
    "POST",
    {
      name: MOJ_USER_NAME,
      phone: MOJ_PHONE_NO,
      appVersion: MOJ_APP_VERSION,
      countryAreaCode: COUNTRY_AREA_CODE,
      language: MOJ_USER_LANGUAGE,
      deviceId,
    },
    {
      "CLIENT-TYPE": MOJ_CLIENT,
      "X-TENANT": tenant,
    }
  );
  const { payload } = signupData;
  const { id, secret, access_token, refresh_token } = payload;
  profileStore.updateState({
    isTempAuth: true,
    userId: id,
    passCode: secret,
    accessToken: access_token,
    refreshToken: refresh_token,
    tenant,
    isAuth: false,
    userName: "",
  });
}

/**
 *
 *
 */
export async function logout(redirectToLogin = false, options = {}) {
  const profileState = get(profileStore);
  if (profileState.isTempAuth || profileState.isAuth) {
    await apiRequest(
      fetch,
      `${apiUrl}/account-service/v2.0.0/user/logout`,
      "POST",
      null,
      {
        "CLIENT-TYPE": MOJ_CLIENT,
        deviceId: profileState.deviceId,
      },
      false,
      false,
      true
    );
    profileStore.clearState();
    Cookies.remove(AUTH_KEY);
  }
  if (redirectToLogin) {
    const { redirectUrl } = options;
    window?.location.replace(`/login?redirect=${redirectUrl ?? "/"}`);
  }
}

/**
 * @param {function} fetch
 * @param {string} payload
 * @returns {object}
 */
export async function sendOtp(fetch, payload) {
  const profileState = get(profileStore);
  try {
    const userData = await apiRequest(
      fetch,
      `${apiUrl}/requestType62`,
      "POST",
      {
        message: {
          smsRetrieverApi: true,
          checkMultipleUsers: false,
          countryAreaCode: "91",
          ph: `91${payload}`,
          s: 1,
        },
      },
      {
        "device-id": profileState.deviceId,
      }
    );
    if (userData.err) {
      return { err: userData.err };
    }
    return { success: "success" };
  } catch (err) {
    return { err };
  }
}

/**
 * @param {function} fetch
 * @param {object} payload
 * @returns {object}
 */
export async function verifyOtp(fetch, payload) {
  const profileState = get(profileStore);
  const { otpNo, phoneNo } = payload;
  try {
    const userData = await apiRequest(
      fetch,
      `${apiUrl}/account-service/v2.0.0/verifyUserGenOtpV2`,
      "POST",
      {
        authMechanism: "truecaller",
        client: "web",
        otp: otpNo,
        countryCode: "91",
        phoneNo: `91${phoneNo}`,
        policyVersion: "1.0",
        referrer: MOJ_CLIENT,
        signup: false,
      },
      {
        "device-id": profileState.deviceId,
      }
    );
    if (!userData.payload) {
      return { err: userData?.message?.errMsg };
    }
    const { payload: userPayload } = userData;
    const { id, secret, name, access_token, refresh_token } = userPayload;
    const isDeleteInitiated =
      profileState.isDeleteInitiated && !profileState.isAuth
        ? false
        : undefined;
    profileStore.updateState({
      isAuth: true,
      authProvider: "phone",
      isTempAuth: true,
      userId: id,
      passCode: secret,
      accessToken: access_token,
      refreshToken: refresh_token,
      isOtpVerified: true,
      userName: name,
      isDeleteInitiated,
    });
    return { success: "success" };
  } catch (err) {
    return { err };
  }
}

// Login api

/**
 * @param {function} fetch
 * @param {object} payload
 * @returns {object}
 */
export async function socialSignUp(fetch, payload) {
  const profileState = get(profileStore);
  const { authCode, authProvider, webRedirectUri } = payload;
  try {
    const userData = await apiRequest(
      fetch,
      `${apiUrl}/socialSignUp`,
      "POST",
      {
        authMeta: {
          authCode,
          authProvider,
        },
        webRedirectUri,
        deviceId: profileState.deviceId,
      },
      {
        "device-id": profileState.deviceId,
        "client-type": "web",
      }
    );
    if (!userData.payload) {
      return { err: userData?.msg };
    }
    const { payload: userPayload } = userData;
    const { id, secret, name, access_token, refresh_token } = userPayload;
    profileStore.updateState({
      isAuth: true,
      authProvider,
      isTempAuth: true,
      userId: id,
      passCode: secret,
      accessToken: access_token,
      refreshToken: refresh_token,
      isOtpVerified: false,
      userName: name,
    });
    return { success: "success" };
  } catch (err) {
    return { err };
  }
}

/**
 * @param {function} fetch
 * @param {string} productUrl
 * @returns {object}
 */
export async function validateProductUrl(fetch, productUrl) {
  const profileState = get(profileStore);
  const url = `${apiUrl}/a1s-service/v1/external/product-details/get`;
  try {
    const request = {
      method: "POST",
      body: JSON.stringify({
        productUrls: [`${productUrl}`],
      }),
      headers: {
        "Content-Type": "application/json",
        "X-SHARECHAT-AUTHORIZED-USERID": profileState.userId,
        "X-SHARECHAT-USERID": profileState.userId,
        "X-SHARECHAT-SECRET": profileState.passCode,
        "X-SHARECHAT-CALLER": "pwa-moj-service",
      },
    };
    const data = await fetch(url, request);
    const productData = await data.json();
    if (productData[0].errorMessage) {
      return { error: productData[0].errorMessage };
    }
    return {
      success: "success",
      productData: productData[0],
    };
  } catch (error) {
    return { error };
  }
}

export async function togglePostLikeById(fetch, item, action) {
  const profileState = get(profileStore);
  try {
    const data = await apiRequest(fetch, `${apiUrl}/requestType51`, "POST", {
      message: {
        a: item.a,
        l: action ? 1 : -1,
        likeType: "clicked",
        p: item.i,
      },
      userId: profileState.userId,
    });
    return data;
  } catch (error) {
    return { error };
  }
}

export async function getVideoFeed(fetch) {
  const response = await apiRequest(
    fetch,
    `${apiUrl}/videoFeed`,
    "POST",
    {
      message: { lang: FEED_LANGUAGE },
    },
    { "CLIENT-TYPE": MOJ_CLIENT }
  );
  const { payload } = response;
  const { d } = payload;
  return d || [];
}

/**
 * @param {object} fetch
 * @param {string} reducerAction
 * @returns {Array}
 */
export async function getTrendingFeedData(fetch, reducerAction = "replace") {
  const feed = await getVideoFeed(fetch);
  if (reducerAction === "concat") {
    trendingFeedStore.updateState(feed);
  } else {
    trendingFeedStore.setState(feed);
  }
  return feed;
}

/**
 *
 * @param {object} fetch
 * @param {object} param1 - {tagId, offset}
 * @returns {object}
 */
export async function getFeedDataByTagId(fetch, { tagId, offset }) {
  const feedData = await apiRequest(
    fetch,
    `${apiUrl}/tag-service/v1.0.0/tag/${tagId}/feed/popular?lang=English${
      offset ? `&offset=${offset}` : ``
    }`
  );
  tagFeedStore.updateState(feedData);
  return feedData;
}
/**
 *
 * @param {object} fetch
 * @returns {Array}
 */
export async function getMojSpotFaqs(fetch) {
  const faqsData = await apiRequest(
    fetch,
    `${apiUrl}/post-action-service/v1.0.0/public/moj-spot/faq`
  );

  return faqsData?.data;
}
/**
 *
 * @param {object} fetch
 * @param {string} n
 * @returns {object}
 */
export async function getTagIdFromTagName(fetch, n) {
  return apiRequest(
    fetch,
    `${apiUrl}/tag-service/v1.0.0/tagByName?tagName=${n}&projections=tagId`
  );
}

/**
 *
 * @param {object} fetch
 * @param {string} postId
 * @param {string} parentCommentId
 * @param {string} offset
 * @param {string} sortBy
 * @param {string} sortOrder
 * @returns {object}
 */
export async function getCommentsById(
  fetch,
  postId,
  parentCommentId,
  offset = null,
  sortBy = "trending",
  sortOrder = "descending"
) {
  const data = await apiRequest(
    fetch,
    `${apiUrl}/comment/v2.0.0/getCommentByLevel`,
    "POST",
    {
      message: {
        postId,
        offset,
        parentCommentId,
        sortBy,
        sortOrder,
      },
    }
  );
  const { payload } = data;
  const { a, d } = payload;
  return { a, d, offset: payload.offset };
}

/**

 * @param {object} fetch
 * @param {string} videoId
 * @returns {object}
 */
export async function getVideoById(fetch, videoId) {
  const videoData = await apiRequest(fetch, `${apiUrl}/requestType45`, "POST", {
    message: {
      p: videoId,
      r: 0,
    },
  });

  if (videoData.payload) {
    const { payload } = videoData;
    const { d } = payload;

    // In authorIdStatus: “12345678_0” => _0 = discarded
    // Eg: /@ankurkumar184/video/709171815?referrer=share
    if (d && (d.authorIdStatus || "").split("_")[1] === "0") {
      d.isDeleted = true;
    }
    return d;
  }
  return {
    err: "Page Not Found",
  };
}
/**

 * @param {object} fetch
 * @param {string} albumId
 * @returns {object}
 */
export async function getAlbumById(fetch, albumId) {
  const profileState = get(profileStore);
  const albumData = await apiRequest(
    fetch,
    `${apiUrl}/creator-gratification-service/v1.0.0/public/album-details?albumId=${albumId}&language=Hindi`,
    "GET",
    {
      headers: {
        "Content-Type": "application/json",
        "app-version": MOJ_APP_VERSION,
        "X-SHARECHAT-AUTHORIZED-USERID": profileState.userId,
        "X-SHARECHAT-USERID": profileState.userId,
        "X-SHARECHAT-SECRET": profileState.passCode,
      },
    }
  );

  if (albumData.data) {
    const { data } = albumData;
    return data;
  }
  return {
    err: "Page Not Found",
  };
}
/**
 *
 * @param {object} fetch
 * @param {string} audioId
 * @returns {object}
 */
export async function getAudioById(fetch, audioId) {
  const audioData = await apiRequest(
    fetch,
    `${apiUrl}/compose-service/v1.0.0/getTopVideosFromAudioId`,
    "POST",
    {
      message: {
        audioId,
        count: 1,
        page: 0,
        sortBy: "POPULAR",
      },
    }
  );
  const payload = audioData.payload || audioData;
  const { data } = payload || {};
  const { postCards } = data || {};
  return postCards ? postCards[0] : {};
}
/**
 *
 * @param {object} fetch
 * @param {string} creatorId
 * @returns {object}
 */
export async function getStoryById(fetch, creatorId) {
  const profileState = get(profileStore);
  const storyData = await apiRequest(
    fetch,
    `${apiUrl}/moj-story-feed-service/stories/meta?creatorIds=${creatorId}`,
    "GET",
    {
      headers: {
        "X-SHARECHAT-AUTHORIZED-USERID": profileState.userId,
      },
    }
  );
  return storyData?.data;
}
/**
 *
 * @param {object} fetch
 * @param {string} audioId
 * @param {number} offset
 * @param {number} limit
 * @returns {object}
 */
export async function getAudioFeedById(
  fetch,
  deviceId,
  audioId,
  offset = 0,
  limit = 10
) {
  const audioData = await apiRequest(
    fetch,
    `${apiUrl}/audio-feed-service/v1.0.0/audioFeed?audioId=${audioId}&offset=${offset}&limit=${limit}`,
    "GET",
    {},
    { "DEVICE-ID": deviceId }
  );
  const payload = audioData.payload || audioData;
  const d = payload?.data?.audioFeed ?? [];
  return { d, lp: offset + limit };
}

/**
 *
 * @param {object} fetch
 * @param {string} videoId
 * @returns {object}
 */
export async function getLivestreamCardInfo(fetch, path) {
  const streamData = await apiRequest(
    fetch,
    `${apiUrl}/livestream-share-service/v1/internal/livestreamCardInfo`,
    "POST",
    {
      url: path,
    },
    {},
    false
  );

  return streamData;
}

/**
 * @param {object} fetch
 * @param {string} tagId
 * @returns {object}
 */
export async function getTagById(fetch, tagId) {
  const tagData = await apiRequest(
    fetch,
    `${apiUrl}/explore-service/v1.0.0/tag/${tagId}`
  );
  return tagData;
}

/**
 *
 * @param {object} fetch
 * @param {string} handle
 * @returns {object}
 */
export async function getProfileByHandle(fetch, handle) {
  const profileData = await apiRequest(
    fetch,
    `${apiUrl}/requestType56`,
    "POST",
    {
      message: {
        a: handle,
        t: 0,
      },
    }
  );
  if (profileData.payload) {
    const { payload } = profileData;
    const { d } = payload;
    return d;
  }
  return profileData;
}

/**
 *
 * @param {object} fetch
 * @param {number} authorId
 * @returns {object}
 */
export async function getProfileVideosByUserId(fetch, authorId, lp) {
  const data = await apiRequest(fetch, `${apiUrl}/requestType50`, "POST", {
    message: {
      ai: authorId,
      lp,
    },
  });
  const { payload } = data;
  const { d } = payload;
  return { d, lp: payload.lp };
}

// -----------
// WIKI start
// -----------
/**
 * @param {object} fetch
 * @param {string} handle
 * @returns {string}
 */
export async function getWikiUser(fetch, handle) {
  const userData = await sanityRequest(
    fetch,
    sanityApiUrl,
    `query {
      allUser(where:{handle: {eq: "@${handle}"}}) {
        _id 
        _createdAt
        name
        handle
        bio
        fanNote
        displayPic{
          asset{
            url
            altText
            metadata {
              dimensions {
                width
                height
              }
            }
          }
        }
        displayVid{
          asset{
            url
            altText
          }
        }
        slug {
          current
        }
        dob
        age
        nickname
        starsign
        hometown
        fatherName
        motherName
        eyeColor
        hairColor
        height
        weight
        hobbies
        careerDebut
        profession
        social{
          social
          link
        }
        biography{
          question
          answerRaw
        }
      }
    }`
  );
  const { data } = userData;
  const { allUser } = data;
  return allUser && allUser.length ? allUser[0] : null;
}

/**
 *
 * @param {object} fetch
 * @returns {Array}
 */
export async function getWikiQuizData(fetch) {
  const quizData = await sanityRequest(
    fetch,
    sanityApiUrl,
    `query {
      allQuiz(sort: { _createdAt: DESC }) {
        _id
        ques
        options
        ans
      }
    }`
  );
  const { data } = quizData;
  const { allQuiz } = data;
  return allQuiz;
}

/**
 * @param {method} fetch
 * @param {object} param1
 * @returns {Array}
 */
export async function getWikiSearchUsers(fetch, { offset = 0 } = {}) {
  const usersData = await sanityRequest(
    fetch,
    sanityApiUrl,
    `query {
      allUser(sort: { _createdAt: DESC },  where:{visible: {eq: true}}, limit: 9, offset: ${offset}) {
        _id
        handle
        name
        slug{
          current
        }
        bio
        displayPic{
          asset{
            url
            altText
            metadata{
              dimensions{
                width
                height
              }
            }
          }
        }
      }
    }`
  );
  const { data } = usersData;
  const { allUser } = data;
  return allUser.map((item) => ({
    n: item.name,
    h: item.handle,
    s: item.displayPic.asset.url,
    d: item.bio,
  }));
}

/**
 *
 * @param {object} fetch
 * @returns {Array}
 */
export async function getWikiTopRankingData(fetch) {
  const rankingData = await sanityRequest(
    fetch,
    sanityApiUrl,
    `query {
      allRanking(sort: { _createdAt: DESC }, where:{ranking: {eq: "TopCharts"}}) {
        users{
          ... on User {
          _id
          handle
          name
          slug{
            current
          }
          bio
          displayPic{
            asset{
              url
              altText
              metadata{
                dimensions{
                  width
                  height
                }
              }
            }
          }
        }
      }
      }
    }`
  );
  const { data } = rankingData;
  const { allRanking } = data;
  return allRanking && allRanking.length > 0
    ? allRanking[0].users.map((item) => ({
        n: item.name,
        h: item.handle,
        s: item.displayPic.asset.url,
        d: item.bio,
      }))
    : [];
}

/**
 *
 * @param {object} fetch
 * @param {string} id
 * @returns {Array}
 */
export async function getWikiRelatedUsers(fetch, id) {
  const usersData = await sanityRequest(
    fetch,
    sanityApiUrl,
    `query {
      allUser(sort: { _createdAt: DESC }, where:{visible: {eq: true}}, limit: 4) {
        _id
        handle
        name
        slug{
          current
        }
        bio
        displayPic{
          asset{
            url
            altText
            metadata{
              dimensions{
                width
                height
              }
            }
          }
        }
      }
    }`
  );
  const { data } = usersData;
  const { allUser } = data;
  return allUser
    .filter((item) => item._id !== id)
    .slice(0, 3)
    .map((item) => ({
      n: item.name,
      h: item.handle,
      s: item.displayPic.asset.url,
      d: item.bio,
    }));
}

// -----------
// MFC start
// -----------
/**
 *
 * @param {object} fetch
 * @returns {object} return creator's data
 */
export async function fetchBuddingData(fetch) {
  const res = await apiRequestWithAuth(
    fetch,
    `${apiUrl}${buddingCreaterProgramUrl}?language=English`,
    "GET",
    null
  );
  return res.data;
}

/**
 *
 * @param {number} applicationId
 * @param {string} categoryName
 * @returns if request is submitted successfully returns SUCCESS
 */
export async function setBuddingCreatorCategoryStatus(
  applicationId,
  categoryName
) {
  const categoryStatus = await apiRequestWithAuth(
    window.fetch,
    `${apiUrl}${buddingCreaterProgramUrl}/${applicationId}/category`,
    "PATCH",
    {
      category: categoryName,
    }
  );
  const {
    data: { status },
  } = categoryStatus;
  return status;
}

/**
 *
 * @param {number} applicationId
 * @param {object} statusVal
 * @returns {string} if request is submitted successfully returns SUCCESS
 */
export async function submitBuddingCreatorRequest(applicationId, statusVal) {
  const submitStatus = await apiRequestWithAuth(
    window.fetch,
    `${apiUrl}${buddingCreaterProgramUrl}/${applicationId}/status`,
    "PATCH",
    {
      status: statusVal,
    }
  );
  const {
    data: { status },
  } = submitStatus;

  return status;
}

// -----------
// NPS start
// -----------
/**
 *
 * @param {object} fetch
 * @returns {Array} campaign page data array
 */
export async function getPunjabiStarFinalists(fetch) {
  const rankingData = await sanityRequest(
    fetch,
    sanityApiUrl,
    `query {
      allRanking(sort: { _createdAt: DESC }, where:{ranking: {eq: "MojNextPunjabiStarVoting"}}) {
        users {
          ... on Video {
            _id
            userHandle
            src
            poster {
              asset {
                url
                altText
                metadata {
                  dimensions {
                    width
                    height
                  }
                }
              }
            }
            bestFrame
          }
        }
      }
    }`,
    {}
  );
  const { data } = rankingData;
  const { allRanking } = data;
  return allRanking && allRanking.length > 0
    ? allRanking[0].users
        .filter((item) => Object.keys(item).length !== 0)
        .map((item) => ({
          i: item._id,
          h: item.userHandle,
          b: `${
            item.poster ? item.poster.asset.url : item.bestFrame
          }?auto=format&w=740&fit=max`,
          s: item.src,
        }))
    : [];
}

/**
 *
 * @param {object} fetch
 * @param {number} id
 */

// -----------
// KS start
// -----------
/**
 *
 * @param {object} fetch
 * @returns {Array} campaign page data array
 */
export async function getKnowledgeSaptahLeaderboard(fetch) {
  const rankingData = await sanityRequest(
    fetch,
    sanityApiUrl,
    `query {
      allRanking(sort: { _createdAt: DESC }, where:{ranking: {eq: "KnowledgeSaptah"}}) {
        users {
          ... on Video {
            userHandle
            postId
            src
            poster {
              asset {
                url
                altText
                metadata {
                  dimensions {
                    width
                    height
                  }
                }
              }
            }
          }
        }
      }
    }`
  );
  const { data } = rankingData;
  const { allRanking } = data;
  return allRanking && allRanking.length > 0 ? allRanking[0].users : [];
}

// -----------
// GOM start
// -----------
/**
 *
 * @param {object} fetch
 * @returns {Array} campaign page data array
 */
export async function getGamingOnMoj(fetch) {
  const rankingData = await sanityRequest(
    fetch,
    sanityApiUrl,
    `query {
      allRanking(sort: { _createdAt: DESC }, where:{ranking: {eq: "GamingOnMoj"}}) {
        users {
          ... on Video {
            userHandle
            postId
            src
            tag{
              name
            }
            poster {
              asset {
                url
                altText
                metadata {
                  dimensions {
                    width
                    height
                  }
                }
              }
            }
          }
        }
      }
    }`
  );
  const { data } = rankingData;
  const { allRanking } = data;
  const arr = allRanking && allRanking.length > 0 ? allRanking[0].users : [];
  const obj = {};
  arr.forEach((el) => {
    if (el && el.tag) {
      obj[el.tag.name] = (obj[el.tag.name] || []).concat(el);
    }
  });
  const predefinedOrder = ["PubG", "FreeFire"];
  return predefinedOrder
    .concat(Object.keys(obj).filter((el) => !predefinedOrder.includes(el)))
    .map((k) => ({
      n: k,
      d: obj[k],
    }));
}

// -----------
// Food IP start
// -----------
/**
 *
 * @param {object} fetch
 * @returns {object} campaign page data object
 */
export async function getKitchenMinisters(fetch) {
  const rankingData = await sanityRequest(
    fetch,
    sanityApiUrl,
    `query {
      allRanking(
        sort: { _createdAt: DESC }
        where: { ranking: { eq: "KitchenMinisters" } }
      ) {
        users {
          ... on Video {
            userHandle
            postId
            src
            caption
            tag{
              name
            }
            poster {
              asset {
                url
                altText
                metadata {
                  dimensions {
                    width
                    height
                  }
                }
              }
            }
          }        
        }
      }
    }`
  );
  const { data } = rankingData;
  const { allRanking } = data;
  const arr = allRanking && allRanking.length > 0 ? allRanking[0].users : [];

  const leaderTags = [
    "Swag",
    "Fashionable",
    "Chopping",
    "Humorous",
    "Presentable",
    "Cuteness",
  ];
  const staticVideos = ["Expert", "HowToParticipate"];

  // videos of leaderTags, staticVideos and leaderboard videos
  const videoArr = arr.filter(
    (el) =>
      (el.tag && el.caption == null) ||
      leaderTags.includes(el.tag.name) ||
      staticVideos.includes(el.tag.name)
  );

  // Photo/video of state host having caption
  const hostArr = arr.filter(
    (el) =>
      el.tag &&
      el.caption !== null &&
      !leaderTags.includes(el.tag.name) &&
      !staticVideos.includes(el.tag.name)
  );

  const states = [];
  const obj = {};
  videoArr.forEach((el) => {
    obj[el.tag.name] = (obj[el.tag.name] || []).concat(el);
  });

  // This will contain all leaderboard videos statewise
  const statesObjArr = states
    .concat(
      Object.keys(obj).filter(
        (el) => !leaderTags.includes(el) && !staticVideos.includes(el)
      )
    )
    .sort()
    .map((k) => ({
      n: k,
      d: obj[k],
    }));

  const leaderObjArr = leaderTags.map((k) => ({ n: k, d: obj[k] || null }));
  const staticVideoArr = staticVideos.map((k) => ({ n: k, d: obj[k] || null }));
  // console.log({ leaderObjArr, statesObjArr, hostArr, staticVideoArr });
  const allData = {
    statesObjArr,
    leaderObjArr,
    hostArr,
    staticVideoArr,
  };

  return allData;
}

// ----------------------------
// Generic Landing Page Start
// ----------------------------
/**
 *
 * @param {object} fetch
 * @param {string} name
 * @returns {object} campaign page data or error object
 */
export async function getLandingPageData(fetch, name) {
  const widgetData = await sanityRequest(
    fetch,
    sanityApiUrl,
    `
		query {
					allGenericLandingPage(
					  sort: { _createdAt: DESC }
					  where: { campaignName: { eq: "${name}" } }
					) {
					  campaignTheme
					  voteButtonTextColor
					  voteButtonBgColor
					  buttonBeforeText
					  buttonAfterText
					  widgets {
						... on GenericImageWidget {
						  desktopBgImage{
							asset{
							  	url
								metadata{
									dimensions{
										aspectRatio
									}
								}
							}
						  }
						  mobileBgImage{
							 asset{
							  	url
								metadata{
									dimensions{
										aspectRatio
									}
								}
							}
						  }
						 link
						  postId
						  src
						  poster{
							asset{
							  	url
								metadata{
									dimensions{
										aspectRatio
									}
								}
							}
						  }
						  caption
						  title
						  subtitle
						  alignment
						  fontColor
						}
						...on GenericLeaderboardWidget{
						   desktopBgImage{
								asset{
									url
									metadata{
										dimensions{
											aspectRatio
										}
									}
								}
						  	}
							mobileBgImage{
								asset{
									url
									metadata{
										dimensions{
											aspectRatio
										}
									}
								}
							}
							title
							subtitle
							autoplay
							captionFontColor
							videos{
								...on Video{
									_id
									src
									votingEnabled
									actionButton
									params
									mojLink
									caption
									userHandle
									poster{
										asset{
											url
											metadata{
												dimensions{
													aspectRatio
												}
											}
										}
									}
								}
							}
						}
						...on GenericCategoriesWidget{
						  title
						  textColor
						  bgc
						  state
						  categories{
							...on GenericCategoryWidget{
							  categoryName
							  categoryItems{
								...on GenericImageWidget{
								  desktopBgImage{
									asset{
									  	url
										metadata{
											dimensions{
												aspectRatio
											}
										}
									}
								  }
								  mobileBgImage{
									asset{
									  	url
										metadata{
											dimensions{
												aspectRatio
											}
										}
									}
								  }
								  link
								  postId
								  src
								  poster{
									asset{
									  	url
										metadata{
											dimensions{
												aspectRatio
											}
										}
									}
								  }
								  caption
								  title
								  subtitle
								  alignment
								  fontColor
								}
								...on GenericLeaderboardWidget{
								  desktopBgImage{
									asset{
									  	url
										metadata{
											dimensions{
												aspectRatio
											}
										}
									}
								  }
								  mobileBgImage{
									asset{
									  	url
									  	metadata{
											dimensions{
												aspectRatio
											}
										}
									}
								  }
								  title
								  subtitle
								  autoplay
								  captionFontColor
								  videos{
									...on Video{
									  _id
									  src
									  votingEnabled
									  actionButton
									  params
									  postId
									  userHandle
									  caption
									  poster{
										asset{
										  	url
											metadata{
												dimensions{
													aspectRatio
												}
											}
										}
									  }
									}
								  }
								}
							  }
							}
						  }
						}
						...on GenericTimerWidget{
						  	launchAt
						  	text
							desktopBgImage{
								asset{
									url
									metadata{
										dimensions{
											aspectRatio
										}
									}
								}
							}
							mobileBgImage{
								asset{
									url
									metadata{
										dimensions{
											aspectRatio
										}
									}
								}
							}
						}
						...on GenericListLeaderboardWidget{
							title
							backgroundColor
							leaderboardId
							leaderboardType
							fontColor
							limit
						  }
					  }
					}  
				  }`
  );
  const { data } = widgetData;
  const { allGenericLandingPage } = data;
  if (!allGenericLandingPage || allGenericLandingPage.length === 0) {
    return {
      err: "Page Not Found",
    };
  }

  const campaignTheme = allGenericLandingPage[0].campaignTheme || "#ff0b49";
  const voteButtonTextColor =
    allGenericLandingPage[0].voteButtonTextColor || "#ffffff";
  const voteButtonBgColor =
    allGenericLandingPage[0].voteButtonBgColor || "#000000";
  const buttonBeforeText =
    allGenericLandingPage[0].buttonBeforeText || "Notify";
  const buttonAfterText = allGenericLandingPage[0].buttonAfterText || "Done";

  const allData =
    allGenericLandingPage && allGenericLandingPage.length > 0
      ? allGenericLandingPage[0].widgets
      : [];

  const landingPageData = allData.map((ele) => {
    if (ele.leaderboardId) {
      return {
        ...ele,
        type: WIDGET_TYPES.LIST_LEADERBOARD,
      };
    }
    if (ele.launchAt) {
      return {
        ...ele,
        type: WIDGET_TYPES.TIMER,
      };
    }
    if (ele.categories) {
      return {
        ...ele,
        type: WIDGET_TYPES.CATEGORY,
      };
    }
    if (!ele.src && !ele.videos && !ele.caption) {
      if (!ele.link) {
        return {
          ...ele,
          type: WIDGET_TYPES.PHOTO,
        };
      }
      return {
        ...ele,
        type: WIDGET_TYPES.PHOTO_WITH_LINK,
      };
    }

    if (ele.videos) {
      return {
        ...ele,
        type: WIDGET_TYPES.LEADERBOARD,
      };
    }

    if (ele.src || (ele.poster && ele.caption) || ele.title || ele.subtitle) {
      return {
        ...ele,
        type: WIDGET_TYPES.PHOTO_OR_VIDEO_WITH_CAPTION,
      };
    }
    return { ...ele };
  });

  const pageData = {
    campaignTheme,
    voteButtonTextColor,
    voteButtonBgColor,
    buttonBeforeText,
    buttonAfterText,
    landingPageData,
  };
  return pageData;
}

/**
 *
 * @param {object} fetch
 * @param {number} id
 */
export async function upvoteFinalists(fetch, id, campaignName) {
  const resp = await sanityPostRequest(fetch, [
    {
      patch: {
        id,
        inc: {
          votes: 1,
        },
      },
    },
  ]);
  if (resp && !resp.error) {
    genericLandingPageStore.updateState(id, campaignName, "vote");
  }
}

/**
 *
 * @param {object} fetch
 * @param {number} id
 */
export async function subscribeLivestream(
  fetch,
  id,
  campaignName,
  scheduleId,
  livestreamId
) {
  const userId = Cookies.get("userId");
  const token = Cookies.get("token");
  const appVersion = window.Android ? window.Android.get("appVersion") : 4884;
  const body = JSON.stringify({
    entityId: scheduleId,
    action: "SUBSCRIBE_CURRENT",
    entityType: "LIVESTREAM_SCHEDULE",
    metaData: {
      scheduleId,
      livestreamId,
      referrerComponent: "live_fest_webcard",
      currentScreen: "live_fest_webcard",
    },
  });

  const request = {
    method: "PUT",
    body,
    headers: {
      "Content-Type": "application/json",
      "X-SHARECHAT-AUTHORIZED-USERID": userId,
      "app-version": appVersion,
      "X-SHARECHAT-USERID": userId,
      "X-SHARECHAT-SECRET": token,
    },
  };
  const response = await fetch(
    `${apiUrl}/livestream-subscription-service/v1/public/users/${userId}/subscriptions`,
    request
  );
  const resp = await response.json();
  if (resp && !resp.err) {
    genericLandingPageStore.updateState(id, campaignName, "subscribe");
  }
}

/**
 *
 * @param {string} streamId
 * @returns {object}
 */
export async function enterLivestream(streamId) {
  const resp = await apiRequest(
    window.fetch,
    `${apiUrl}/livestream-service/v1/public/livestreams/${streamId}/enter?referrer=webview-viewer`,
    "POST",
    {},
    { "CLIENT-TYPE": "Android" },
    false
  );
  return resp;
}

/**
 *
 * @param {string} streamId
 * @returns {object}
 */
export async function getLivestreamInfo(streamId) {
  const streamData = await apiRequest(
    window.fetch,
    `${apiUrl}/livestream-service/v1/public/livestreams/${streamId}?referrer=webview-viewer`,
    "GET",
    {},
    { "CLIENT-TYPE": "Android" },
    false
  );
  return streamData;
}

/**
 *
 * @param {string} streamId
 * @returns {object}
 */
export async function pingLivestream(streamId) {
  const streamData = await apiRequest(
    window.fetch,
    `${apiUrl}/ping-service/v1/public/ping`,
    "POST",
    {
      livestreamId: streamId,
      pingTime: Date.now(),
    },
    { "CLIENT-TYPE": "Android" },
    false,
    true
  );
  return streamData;
}

/**
 *
 * @param {object} fetch
 * @param {number} creatorId
 * @returns {object} return trophies and journey data
 */
export async function fetchGamificationDetail(fetch, creatorId, section) {
  const isTrophies = section === TROPHIES;
  const { platform } = getUserData();
  const res = await fetcher(
    fetch,
    `${apiUrl}${gamificationPrefix}${
      isTrophies ? trophiesDetailKey : creatorJourneyKey
    }?creatorId=${creatorId}&ts=${Date.now()}`,
    {
      method: "GET",
      headers: {
        language: MOJ_USER_LANGUAGE,
        "X-TENANT": WEBVIEW_ENVIRONMENT.MOJ,
        CLIENT: platform,
      },
    },

    {
      apiReqWithAuth: true,
    }
  );
  const resp = !isTrophies ? res?.data : res;
  if (isTrophies) {
    gamificationStore.updateState({ trophiesDetails: { ...resp.data } });
  } else {
    gamificationStore.updateState({ level: resp.level });
  }
  return res?.data;
}

export async function getCreatorOptingEligibilityStatus(userId) {
  const profileState = get(profileStore);
  const res = await apiRequestWithAuth(
    window?.fetch,
    `${apiUrl}/creator-monetization-tools-service/v1/external/marketPlaceCreator/creators/is-eligible/${
      userId || profileState?.userId
    }`,
    "GET",
    null
  );
  return res;
}

export async function getCampaignDetails(params) {
  const profileState = get(profileStore);
  return apiRequestWithAuth(
    window?.fetch,
    `${apiUrl}/htc-qc-review-tool-service/v1/users/${
      params.creatorId || profileState?.userId
    }/campaigns//${params.campaignId}/details`,
    "GET",
    null
  );
}

// ----------------------------
// Wallet Page Start
// ----------------------------

/**
 *
 * @param {object} fetch
 * @param {number} creatorId
 * @returns {object} return trophies data
 */

export async function fetchWalletInfo() {
  const res = await apiRequest(
    window?.fetch,
    `${apiUrl}/payments-api-gateway/v1/wallets`,
    "GET",
    null,
    {
      language: MOJ_USER_LANGUAGE,
      "CLIENT-TYPE": "web",
      "CODE-PUSH-VERSION": "V45",
    }
  );
  return res;
}

export async function fetchRechargeListData() {
  const res = await apiRequest(
    window?.fetch,
    `${apiUrl}/payments-api-gateway/v1/products`,
    "GET",
    null,
    {
      language: MOJ_USER_LANGUAGE,
      "CLIENT-TYPE": "web",
    }
  );
  return res;
}

export async function fetchFaqsData() {
  const res = await apiRequest(
    window?.fetch,
    `${apiUrl}/payments-api-gateway/v1/faq/CHEER`,
    "GET",
    null,
    {
      language: MOJ_USER_LANGUAGE,
      "CLIENT-TYPE": "web",
    }
  );
  return res;
}

export async function fetchTransactionInfo(fetch, params) {
  const { limit, currency, offset, category, status, fail } = params;
  const extraPath = `/${currency}`;
  let query = `limit=${limit || 20}`;
  if (category) query += `&category=${category}`;
  if (status) query += `&status=${status}`;
  if (offset) query += `&offset=${offset}`;
  const res = await apiRequest(
    fetch,
    fail
      ? `${apiUrl}/payments-api-gateway/v1/orders?status=PENDING&status=FAILED&${query}`
      : `${apiUrl}/payments-api-gateway/v1/wallets/transactions${extraPath}?${query}`,
    "GET",
    null,
    {
      language: MOJ_USER_LANGUAGE,
      "CLIENT-TYPE": "web",
    }
  );
  return res;
}

export async function fetchTopGifters(fetch, leaderboardId) {
  const url = `${apiUrl}/leaderboard-service/v2/public/platform-leaderboards/campaigns?campaignType=${leaderboardId}`;
  const res = await apiRequest(fetch, url, "GET", {});
  return res;
}

export async function getRechargeCtaVisibilitiy(fetch, userId = "") {
  const url = `${liveStreamVgServiceUrl}/experiment/rechargeCTA/${userId}`;
  return apiRequest(fetch, url, "GET", {});
}

/**
 * This function aims to update the latest selected trophies by user on userProfile.
 * @param {object} payload
 * @return {Promise<import("@/types/gamification.type").TUpdateFetcher>}
 */
export async function updateLatestTrophies(payload = {}) {
  const url = `${apiUrl}${gamificationPrefix}/${RECENT_TROPHIES}`;
  const profileState = get(profileStore);
  const headers = {
    "X-TENANT": profileState.tenant || constants.TENANTS.MOJ,
  };
  return apiRequestWithAuth(window.fetch, url, "POST", payload, headers);
}

export async function fetchExperimentVariant(id) {
  const isExperimentList = Array.isArray(id);
  const experimentId = new URLSearchParams(
    isExperimentList
      ? id.map((experiment) => `expId=${experiment}`).join("&")
      : `expId=${id}`
  ).toString();
  try {
    const response = await fetch(`/api/fetch-experiment-data?${experimentId}`, {
      method: "POST",
      headers: {
        "content-type": "application/json",
      },
    });
    const experimentResponse = await response.json();
    return isExperimentList
      ? experimentResponse.response
      : experimentResponse.response[0]?.expList[0]?.variant;
  } catch (e) {
    return isExperimentList ? [] : false;
  }
}

export async function fetchUserAuthByUUID(fetch, uuid) {
  try {
    const body = JSON.stringify({
      grantType: "authorizationCode",
      uuid: uuid,
    });
    const request = {
      method: "POST",
      body,
      headers: {
        "Content-Type": "application/json",
        "APP-VERSION": MOJ_APP_VERSION,
        "AUTH-VERSION": "V2",
        "CLIENT-TYPE": "web",
        CLIENT: "web",
        "USER-AGENT": "web",
        "x-sharechat-caller": "pwa-moj",
      },
    };
    const response = await fetch(
      `${apiUrl}/auth-service/public/v2/token`,
      request
    );
    let data = await response.json();
    return data;
  } catch (e) {
    console.log(JSON.stringify(e));
    window?.location.replace("/login");
  }
}

export async function fetchMfc(payload = {}) {
  const url = `${apiUrl}/budding-creator-service/v1.0.0/external/bcp-details/status`;
  return apiRequestWithAuth(fetch, url, "POST", payload);
}

export async function fetchLiveAccess() {
  const url = `${apiUrl}/creator-gamification-service/v1.0.0/public/live-access`;
  const payload = {
    liveAccess: "inReview",
  };
  return apiRequestWithAuth(fetch, url, "POST", payload);
}

export async function fetchIPLBoostData(fetch) {
  const url = `${apiUrl}/creator-monetization-tools-service/v1/public/hashtag/leaderboard`;
  return apiRequest(fetch, url);
}

export async function splashScreenConfig() {
  const url = `${apiUrl}/splashScreenConfig`;
  const profileState = get(profileStore);
  return apiRequest(fetch, url, "POST", {
    client: "web",
    message: {
      appVersion: MOJ_APP_VERSION,
      language: MOJ_USER_LANGUAGE,
      userId: profileState?.userId,
    },
  });
}
